import { Flex } from '@mantine/core';
import { Fragment, useMemo, useCallback } from 'react';
import { BORDER_COLOR, LEFT_SECTION_WIDTH } from '../tableConstants';
import { getColumnValues, parseQuestionDisplayName } from '../tableUtils';
import { useCellNavigation } from '../useCellNavigation';
import { SingleFundHeaderRow } from '../components/SingleFundHeaderRow';
import { MultiFundRow } from '../components/MultiFundRow';
import { SingleFundRow } from '../components/SingleFundRow';
import { QuestionHeader } from '../components/QuestionHeader';

export const RankingTable = ({
  schema,
  fundId,
  isMultiFunds,
  funds,
  tableName,
  onChangeFund,
  questionLevels = [],
  rankLevels = [],
  name,
  isQuestionOpen = true,
  onToggleQuestion
}) => {
  const fundKeys = useMemo(() => Object.keys(funds || {}), [funds]);

  const { cellRefs, registerCell, onCellKeyDown } = useCellNavigation({ isMultiFunds, fundKeys });

  const onCellChange = useCallback(
    (column, value, _fundId, index) => {
      const targetFund = isMultiFunds ? _fundId : fundId;
      const table = funds?.[targetFund]?.fund_data?.[tableName];
      if (!Array.isArray(table) || table[index] == null) return;
      if (table[index]?.[column] === value) return;

      const newTable = [...table];
      newTable[index] = { ...newTable[index], [column]: value };

      onChangeFund(tableName, newTable, isMultiFunds ? _fundId : null);
    },
    [isMultiFunds, fundId, funds, tableName, onChangeFund]
  );

  const rows = funds?.[fundId]?.fund_data?.[tableName] ?? [];
  const { questionNumber, questionName } = parseQuestionDisplayName(name);
  if (isMultiFunds) {
    const isWidthFit =
      fundKeys.length > 3 && window.innerWidth < fundKeys.length * 260 + LEFT_SECTION_WIDTH;
    return (
      <Flex
        direction="column"
        w={isWidthFit ? 'fit-content' : 'auto'}
        bd={`1px solid ${BORDER_COLOR}`}
      >
        <QuestionHeader
          name={questionName}
          number={questionNumber}
          isMultiFunds={isMultiFunds}
          fundKeys={fundKeys}
          funds={funds}
          isOpen={isQuestionOpen}
          onToggle={onToggleQuestion}
        />
        {isQuestionOpen &&
          questionLevels.map((question, index) => {
            const keyPrefix = question.level || question.name;
            return (
              <Fragment key={`${tableName}-${index}-multi-view`}>
                {rows.map((_, i) => (
                  <Fragment key={`${question.level}-${i}`}>
                    {renderMultiFundSection({
                      columns: question.columns,
                      children: question.children,
                      schema,
                      onCellChange,
                      levelIndex: 0,
                      funds,
                      fundKeys,
                      tableName,
                      level: question.level || rankLevels[i],
                      name: question.name,
                      dataIndex: i,
                      cellRefs: { current: cellRefs, register: registerCell },
                      onCellKeyDown,
                      keyPrefix,
                      showQuestionHeader: i === 0
                    })}
                  </Fragment>
                ))}
              </Fragment>
            );
          })}
      </Flex>
    );
  }

  return (
    <Flex direction="column" bd={`1px solid ${BORDER_COLOR}`}>
      <QuestionHeader
        number={questionNumber}
        name={questionName}
        isMultiFunds={isMultiFunds}
        isOpen={isQuestionOpen}
        onToggle={onToggleQuestion}
      />
      {isQuestionOpen &&
        questionLevels.map((question, i) => {
          const keyPrefix = question.level ?? question.name;

          return (
            <Fragment key={`${tableName}-${i}`}>
              {rows.map((row, index) => (
                <Fragment key={`question-${i}-${index}`}>
                  {renderSingleFundSection({
                    columns: question.columns,
                    children: question.children,
                    schema,
                    columnValues: question.children
                      ? {}
                      : getColumnValues(row, Object.keys(question.columns)),
                    onCellChange,
                    levelIndex: 0,
                    level: question.level || rankLevels[index],
                    name: question.name,
                    row,
                    dataIndex: index,
                    cellRefs: { current: cellRefs, register: registerCell },
                    onCellKeyDown,
                    keyPrefix,
                    showQuestionHeader: index === 0,
                    fund: funds?.[fundId],
                    tableName
                  })}
                </Fragment>
              ))}
            </Fragment>
          );
        })}
    </Flex>
  );
};

const renderMultiFundSection = ({
  columns,
  schema,
  onCellChange,
  levelIndex,
  funds,
  fundKeys,
  tableName,
  level,
  name,
  children,
  dataIndex,
  cellRefs,
  onCellKeyDown,
  keyPrefix,
  showQuestionHeader = true
}) => {
  return (
    <>
      {!children &&
        Object.keys(columns).map((column, columnIndex) => (
          <MultiFundRow
            key={column}
            columnSchema={columns[column]}
            cellSchema={schema.properties[column]}
            levelIndex={level ? (columnIndex === 0 ? levelIndex : levelIndex + 1) : levelIndex}
            fundKeys={fundKeys}
            label={
              columns[column].conditions
                ? columns[column].conditions[
                    Object.keys(columns[column].conditions).find(key => +key >= dataIndex)
                  ]
                : columns[column].name
            }
            columnLevel={columnIndex === 0 ? level : columns[column].level}
            getValue={fundKey => funds?.[fundKey]?.fund_data?.[tableName]?.[dataIndex] || {}}
            onFundChange={(fundKey, value) => onCellChange(column, value, fundKey, dataIndex)}
            cellRefs={cellRefs}
            getCellKey={fundKey => `${keyPrefix}-${column}-${fundKey}-${dataIndex}`}
            onCellKeyDown={onCellKeyDown}
            readOnly={column === 'ranking'}
            funds={funds}
            tableName={tableName}
            fieldName={column}
            hasDrillDownFeature={schema.properties[column]?.has_drill_down_feature}
            symbol={schema.properties[column]?.symbol}
          />
        ))}

      {children &&
        children.map(question =>
          renderMultiFundSection({
            columns: question.columns,
            children: question.children,
            schema,
            onCellChange,
            levelIndex: name || level ? levelIndex + 1 : levelIndex,
            funds,
            fundKeys,
            tableName,
            level: question.level,
            name: question.name,
            dataIndex,
            cellRefs,
            onCellKeyDown,
            keyPrefix: question.level ?? question.name,
            showQuestionHeader
          })
        )}
    </>
  );
};

const renderSingleFundSection = ({
  columns,
  schema,
  columnValues,
  onCellChange,
  levelIndex,
  level,
  name: questionName,
  children,
  row,
  dataIndex,
  cellRefs,
  onCellKeyDown,
  keyPrefix,
  showQuestionHeader = true,
  fund,
  tableName
}) => {
  return (
    <>
      {showQuestionHeader && <SingleFundHeaderRow levelIndex={levelIndex} name={questionName} />}
      {!children &&
        Object.keys(columns).map((column, columnIndex) => (
          <SingleFundRow
            key={column}
            columnSchema={columns[column]}
            cellSchema={schema.properties[column]}
            levelIndex={level ? (columnIndex === 0 ? levelIndex : levelIndex + 1) : levelIndex}
            label={
              columns[column].conditions
                ? columns[column].conditions[
                    Object.keys(columns[column].conditions).find(key => +key >= dataIndex)
                  ]
                : columns[column].name
            }
            columnLevel={columnIndex === 0 ? level : columns[column].level}
            value={columnValues[column]}
            error={columnValues?.errors?.[column]}
            onChange={value => onCellChange(column, value, null, dataIndex)}
            cellRefs={cellRefs}
            cellKey={`${keyPrefix}-${column}-${dataIndex}`}
            onCellKeyDown={onCellKeyDown}
            showIndent={Boolean(level)}
            readOnly={column === 'ranking'}
            fundId={fund?.fund_id}
            fundName={fund?.fund_name}
            tableName={tableName}
            fieldName={column}
            hasDrillDownFeature={schema.properties[column]?.has_drill_down_feature}
            tableId={columnValues['id']}
            reportId={columnValues['report_id']}
            isFromStaticTemplate={columnValues?.['static_template_fields']?.includes(column)}
            symbol={schema.properties[column]?.symbol}
          />
        ))}

      {children &&
        children.map(question => (
          <Fragment key={question.level}>
            {renderSingleFundSection({
              columns: question.columns,
              children: question.children,
              schema,
              columnValues: getColumnValues(row, Object.keys(question.columns)),
              onCellChange,
              levelIndex: levelIndex + 1,
              level: question.level,
              name: question.name,
              row,
              dataIndex,
              cellRefs,
              onCellKeyDown,
              keyPrefix: question.level ?? question.name,
              showQuestionHeader,
              fund,
              tableName
            })}
          </Fragment>
        ))}
    </>
  );
};
